import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Pagination,
  AppBar,
  Toolbar,
  Badge,
  CircularProgress,
  useTheme,
  useMediaQuery,
  styled,
  InputAdornment,
  Chip,
  Dialog,  
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LeftToolbar from "./LeftToolbar";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TextEditorComponent from "./TextEditorComponent";
import SearchIcon from "@mui/icons-material/Search";
import NotificationSnackbar from "./NotificationBar";
import { alpha } from "@mui/material/styles";
import ConfirmationDialog from "./ConfirmationDialog";
import DOMPurify from 'dompurify';
import { useSelector } from "react-redux";
import { jsonToHtml } from "./utils/tiptapConfig";

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

function Questions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [completeQuestions, setCompleteQuestions] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loadingFaqId, setLoadingFaqId] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    moduleId: "",
    content: null,
    htmlContent: "",
  });
  const [formErrors, setFormErrors] = useState({
    title: false,
    moduleId: false,
    content: false,
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("Questions");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  const createEditorRef = useRef(null);
  const editEditorRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [user] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : { id: 1, username: "Guest" };
    } catch (error) {
      console.error("Error parsing user data:", error);
      return { id: 1, username: "Guest" };
    }
  });

  // Get data from Redux store
  const ReduxQuestions = useSelector((state) => state.support.filteredFaqs || []); 
  const ReduxCategories = useSelector((state) => state.support.categories || []);
  const isLoading = useSelector((state) => state.support.loading.global);
  console.log("ReduxQuestions:", ReduxQuestions);

  useEffect(() => {
    const fetchQuestions = async () => {
      const newReduxQuestions = await Promise.all(
        ReduxQuestions.map(async (question) => {
          try {
            return {
              ...question,
              title: question.title || 'Untitled Question',
              createdBy: question.createdBy || 'Unknown Author',
              createdOn: question.createdOn || new Date().toISOString(),
              categoryName: question.categoryName || 'Uncategorized',
            };
          } catch (error) {
            console.error(`Error fetching question ${question.id}:`, error);
            return {
              ...question,
              title: question.title || 'Untitled Question',
              createdBy: question.createdBy || 'Unknown Author',
              createdOn: question.createdOn || new Date().toISOString(),
              categoryName: question.categoryName || 'Uncategorized',
              content: 'Content unavailable'
            };
          }
        })
      );
      console.log("Fetched Questions:", newReduxQuestions);
      setCompleteQuestions(newReduxQuestions);
    };

    if (ReduxQuestions.length > 0) {
      fetchQuestions();
    }
  }, [ReduxQuestions, ReduxCategories]);

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setFormData({
      title: "",
      moduleId: "",
      content: null,
      htmlContent: "",
    });
    setFormErrors({
      title: false,
      moduleId: false,
      content: false,
    });
    if (createEditorRef.current) {
      createEditorRef.current.clearEditor();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (value) {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const handleEditorChange = ({ html, json }) => {
    setFormData(prev => ({
      ...prev,
      content: json,
      htmlContent: html,
    }));

    const isContentTrulyEmpty = !json || json.content.length === 0 ||
      (json.content.length === 1 && json.content[0].type === 'paragraph' && !json.content[0].content);

    setFormErrors(prev => ({ ...prev, content: isContentTrulyEmpty }));
  };

  const validateForm = async () => {
    const errors = {
      title: !formData.title.trim(),
      moduleId: !formData.moduleId,
      content: !formData.content || formData.content.content.length === 0 ||
        (formData.content.content.length === 1 && formData.content.content[0].type === 'paragraph' && !formData.content.content[0].content),
    };
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

   const handleCreateQuestion = async () => {
  if (!await validateForm()) {
    handleSnackbarOpen("Please fill all required fields", "error");
    return;
  }

  const userToken = localStorage.getItem("userToken");
  if (!userToken) {
    handleSnackbarOpen("Unauthorized. Please login first.", "error");
    return;
  }

  setLoading(true);
  try {
    const currentEditorContentJson = createEditorRef.current?.getContents();

    if (!currentEditorContentJson || currentEditorContentJson.content.length === 0 ||
        (currentEditorContentJson.content.length === 1 && 
         currentEditorContentJson.content[0].type === 'paragraph' && 
         !currentEditorContentJson.content[0].content)) {
      handleSnackbarOpen("Content cannot be empty.", "error");
      setFormErrors(prev => ({ ...prev, content: true }));
      setLoading(false);
      return;
    }

      const payload = {
      title: formData.title.trim(),
      moduleId: parseInt(formData.moduleId),
      response: JSON.stringify(currentEditorContentJson),
      userId: user.id,   
    };
    console.log("User ID:", user.id);

    console.log("Creating question with payload:", payload);

    const res = await axios.post("/api/support/faq", payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      withCredentials: true 
    });

    if (res.status !== 201) {
      throw new Error(res.data.message || "Failed to create question");
    }

    const newQuestion = {
      id: Date.now(), // Adjust based on your actual response structure
      title: formData.title,
      content: currentEditorContentJson,
      htmlContent: formData.htmlContent,
      moduleId: parseInt(formData.moduleId),
      moduleName: ReduxCategories.find(c => c.id === parseInt(formData.moduleId))?.categoryName || "",
      createdBy: user.username || 'Unknown',
      createdOn: new Date().toISOString(),
    };

    setQuestions(prev => [newQuestion, ...prev]);
    handleSnackbarOpen(res.data.message || "Question created successfully!", "success");
    handleCloseCreateModal();
  } catch (error) {
    console.error("Error creating question:", error);
    let errorMessage = "Failed to create question";
    if (error.response) {
      errorMessage = error.response.data.message || errorMessage;
    } else if (error.request) {
      errorMessage = "No response from server";
    }
    handleSnackbarOpen(errorMessage, "error");
  } finally {
    setLoading(false);
  }
};

  const handleOpenEditQuestionModal = (question) => {
    setEditingQuestion(question);
    setFormData({
      title: question.title,
      moduleId: question.moduleId || "",
      content: question.content,
      htmlContent: question.htmlContent || "",
    });
    setFormErrors({
      title: false,
      moduleId: false,
      content: false,
    });
    setEditModalOpen(true);
  };

  const handleUpdateQuestion = async () => {
    if (!await validateForm()) {
      handleSnackbarOpen("Please fill all required fields", "error");
      return;
    }
    if (!editingQuestion) return;

    setLoading(true);
    try {
      const currentEditorContentJson = editEditorRef.current?.getContents();

      if (!currentEditorContentJson || currentEditorContentJson.content.length === 0 ||
          (currentEditorContentJson.content.length === 1 && currentEditorContentJson.content[0].type === 'paragraph' && !currentEditorContentJson.content[0].content)) {
        handleSnackbarOpen("Content cannot be empty.", "error");
        setFormErrors(prev => ({ ...prev, content: true }));
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title.trim(),
        moduleId: parseInt(formData.moduleId),
        content: JSON.stringify(currentEditorContentJson),
      };

      const res = await axios.put(
        `/api/support/faq/${editingQuestion.id}`,
        payload
      );
      const updatedData = res.data.data || res.data.updatedQuestion;

      setQuestions(questions.map(question =>
        question.id === editingQuestion.id
          ? {
              ...question,
              ...updatedData,
              content: typeof updatedData.content === 'string' ? JSON.parse(updatedData.content) : updatedData.content,
              moduleName: ReduxCategories.find(c => c.id === parseInt(formData.moduleId))?.categoryName || "",
            }
          : question
      ));
      
      handleSnackbarOpen("Question updated successfully!", "success");
      setEditModalOpen(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error("Error updating question:", error);
      handleSnackbarOpen("Failed to update question", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (question) => {
    setLoadingFaqId(question.id); // This will start loading
    try {
      const response = await axios.get(`/api/support/faq/${question.id}`);
      let htmlContent = jsonToHtml(response.data.faq);
      setFormData({
        id: question.id,
        title: question.title,
        module: question.categoryName,
        content: question.response,
        htmlContent: htmlContent || '<p>Content could not be rendered</p>',
      });
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error generating preview:", error);
      handleSnackbarOpen("Failed to generate question preview", "error");
    } finally{
      setLoadingFaqId(null); // This will stop loading
    }
  };

  const handleClosePreview = () => setPreviewOpen(false);

  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setOpenConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;

    setLoading(true);

    try {
      const res = await axios.delete(`/api/support/faq/${questionToDelete.id}`);
      if (res.status === 200) {
        setQuestions(prev => prev.filter(q => q.id !== questionToDelete.id));
        handleSnackbarOpen("Question deleted successfully", "success");
      } else {
        throw new Error(res.data.message || "Failed to delete question");
      }
    } catch (error) {
      console.error("Delete error:", error);
      handleSnackbarOpen("Failed to delete question", "error");
    } finally {
      setLoading(false);
      setOpenConfirm(false);
      setQuestionToDelete(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: theme => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SUPPORT PORTAL
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={ReduxQuestions.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        onCollapse={setCollapsed}
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}>
          <TextField
            label="Search questions..."
            variant="outlined"
            size="small"
            fullWidth={isMobile}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Create new Question">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreateModalOpen(true)}
              startIcon={<AddIcon />}
              size={isMobile ? "small" : "medium"}
            >
              {isMobile ? "Create" : "Create Question"}
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, p: 2, bgcolor: "#f8f9fa", borderRadius: 2 }}>
          <Box sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Questions Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ReduxQuestions.length} {ReduxQuestions.length === 1 ? "Question" : "Questions"} Found
              </Typography>
            </Box>

            <ResponsiveTableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}>
                    {["Title", "Module", "Created Date", "Created By", "Actions"].map(header => (
                      <ModernTableCell
                        key={header}
                        align={header === "Actions" ? "right" : "left"}
                      >
                        {header}
                      </ModernTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <ModernTableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </ModernTableCell>
                    </TableRow>
                  ) : completeQuestions?.length > 0 ? (
                    completeQuestions
                      .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                      .map(question => (
                        <StyledTableRow key={question.id} hover>
                          <ModernTableCell>
                            <Typography fontWeight="medium">
                              {question.title}
                            </Typography>
                          </ModernTableCell>
                          <ModernTableCell>
                            {question.categoryName}
                          </ModernTableCell>
                          <ModernTableCell>
                            {new Date(question.createdOn || question.createdAt).toLocaleDateString()}
                          </ModernTableCell>
                          <ModernTableCell>
                            <Typography variant="body2">
                              {question.createdBy}
                            </Typography>
                          </ModernTableCell>
                          <ModernTableCell align="right">
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              <Tooltip title="View Question">
                                <IconButton
                                  size="small"
                                  color="info"
                                  disabled={loadingFaqId === question.id}
                                  onClick={() => handlePreview(question)}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    },
                                  }}
                                >
                                  {loadingFaqId === question.id ? (
                                                                        <CircularProgress
                                                                          size={20}
                                                                          color="primary"
                                                                        />
                                                                      ) : (
                                                                        <VisibilityIcon fontSize="small" />
                                                                      )}
                                   
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Question">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleOpenEditQuestionModal(question)}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Question">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(question)}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ModernTableCell>
                        </StyledTableRow>
                      ))
                  ) : (
                    <TableRow>
                      <ModernTableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {searchTerm ? "No matching questions found" : "No questions available"}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => setCreateModalOpen(true)}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          Create your first question
                        </Button>
                      </ModernTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ResponsiveTableContainer>

            {ReduxQuestions.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(ReduxQuestions.length / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  shape="rounded"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiPaginationItem-root": { borderRadius: "999px" },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </MainContent>

      {/* Create Question Dialog */}
      <Dialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        BackdropProps={{
  sx: {
    backgroundColor: "rgba(0, 0, 0, 0.6) !important",
  },
}}
        PaperProps={{
          sx: {
            ...(isMobile ? {
              borderRadius: 0,
              background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
              boxShadow: "none",
            } : {
              position: "absolute",
              top: "45%",
              left: "58%",
              transform: "translate(-50%, -50%)",
              borderRadius: "16px",
              background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }),
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{
          p: 0,
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
          },
        }}>
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
          }}>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
            }}>
              Create New Question
            </Typography>
            <IconButton
              onClick={handleCloseCreateModal}
              sx={{
                color: "text.secondary",
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: isMobile ? 2 : 3,
        }}>
          <TextField
            label="Title"
            name="title"
            fullWidth
            size="small"
            value={formData.title}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            error={formErrors.title}
            helperText={formErrors.title ? "Title is required" : ""}
            InputProps={{
              endAdornment: formData.title && (
                <InputAdornment position="end">
                  <CheckCircleIcon color="success" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Select
            fullWidth
            size="small"
            name="moduleId"
            value={formData.moduleId}
            onChange={handleInputChange}
            displayEmpty
            sx={{ mb: 2 }}
            error={formErrors.moduleId}
            renderValue={selected => {
              if (!selected) {
                return (
                  <em style={{
                    color: formErrors.moduleId ? "#f44336" : "rgba(0, 0, 0, 0.6)",
                  }}>
                    Select Module
                  </em>
                );
              }
              return ReduxCategories.find(c => c.id === selected)?.categoryName || selected;
            }}
          >
            <MenuItem value="" disabled>
              Select Module
            </MenuItem>
            {isLoading ? (
              <MenuItem disabled>Loading...</MenuItem>
            ) : (
              ReduxCategories.map(category => (
                <MenuItem key={category.id} value={category.id}>
                  {category.categoryName}
                </MenuItem>
              ))
            )}
          </Select>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Content
            </Typography>
            <TextEditorComponent
              ref={createEditorRef}
              onChange={handleEditorChange}
              initialContent={null}
              maxCharCount={10000}
            />
            {formErrors.content && (
              <Typography color="error" variant="caption">
                Content is required
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseCreateModal}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateQuestion}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 120 }}
          >
            {loading ? "Creating..." : "Create Question"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        BackdropProps={{
  sx: {
    backgroundColor: "rgba(0, 0, 0, 0.6) !important",
  },
}}
        PaperProps={{
          sx: {
            ...(isMobile ? {
              borderRadius: 0,
              background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
              boxShadow: "none",
            } : {
              position: "absolute",
              top: "45%",
              left: "58%",
              transform: "translate(-50%, -50%)",
              borderRadius: "16px",
              background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }),
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{
          p: 0,
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
          },
        }}>
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
          }}>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
            }}>
              Edit Question
            </Typography>
            <IconButton
              onClick={() => setEditModalOpen(false)}
              sx={{
                color: "text.secondary",
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: isMobile ? 2 : 3,
        }}>
          <TextField
            label="Title"
            name="title"
            fullWidth
            size="small"
            value={formData.title}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            error={formErrors.title}
            helperText={formErrors.title ? "Title is required" : ""}
            InputProps={{
              endAdornment: formData.title && (
                <InputAdornment position="end">
                  <CheckCircleIcon color="success" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Select
            fullWidth
            size="small"
            name="moduleId"
            value={formData.moduleId}
            onChange={handleInputChange}
            displayEmpty
            sx={{ mb: 2 }}
            error={formErrors.moduleId}
            renderValue={selected => {
              if (!selected) {
                return (
                  <em style={{
                    color: formErrors.moduleId ? "#f44336" : "rgba(0, 0, 0, 0.6)",
                  }}>
                    Select Module
                  </em>
                );
              }
              return ReduxCategories.find(c => c.id === selected)?.categoryName || selected;
            }}
          >
            <MenuItem value="" disabled>
              Select Module
            </MenuItem>
            {isLoading ? (
              <MenuItem disabled>Loading...</MenuItem>
            ) : (
              ReduxCategories.map(category => (
                <MenuItem key={category.id} value={category.id}>
                  {category.categoryName}
                </MenuItem>
              ))
            )}
          </Select>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Content
            </Typography>
            <TextEditorComponent
              ref={editEditorRef}
              onChange={handleEditorChange}
              initialContent={formData.content}
              maxCharCount={10000}
            />
            {formErrors.content && (
              <Typography color="error" variant="caption">
                Content is required
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditModalOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateQuestion}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 120 }}
          >
            {loading ? "Updating..." : "Update Question"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        BackdropProps={{
  sx: {
    backgroundColor: "rgba(0, 0, 0, 0.6) !important",
  },
}}
      >
        <IconButton
          aria-label="close"
          onClick={handleClosePreview}
          sx={{
            position: 'absolute',
            right: 24,
            top: 24,
            zIndex: 1,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'scale(1.1)',
              boxShadow: '0 0 15px rgba(255,255,255,0.4)'
            },
            '& svg': {
              fontSize: '1.5rem'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ 
  pt: 7,
  pb: 3,
  backgroundColor: 'primary.main',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1,  
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)'
  }
}}>
  <Box sx={{ width: '100%' }}>
    <Typography 
      variant="h5" 
      sx={{
        fontWeight: 600,
        letterSpacing: '0.5px',
        position: 'relative',
        display: 'inline-block',
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: -8,
          left: 0,
          width: '40px',
          height: '3px',
          background: 'white',
          borderRadius: '3px'
        }
      }}
    >
      {formData.title}
    </Typography>
  </Box>
  
  <Chip 
    label={formData.module || "Hello test"} 
    sx={{ 
      color: '#1976D2',
      backgroundColor: 'white',
      fontWeight: 600,
      height: '28px',
      borderRadius: '14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '& .MuiChip-label': {
        px: 1.5,
        py: 0.5
      }
    }} 
  />
</DialogTitle>
        <DialogContent dividers>
          <Box 
            sx={{ 
              p: 2,
              "& img": { maxWidth: "100%" },
              "& pre": { 
                backgroundColor: "#f5f5f5", 
                padding: "16px", 
                borderRadius: "4px",
                overflowX: "auto"
              }
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.htmlContent) }}
          />
        </DialogContent>
         
      </Dialog>

      {/* Confirmation Dialog for Deletion */}
      <ConfirmationDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the question titled "${questionToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />

      {/* Snackbar for notifications */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </MainContainer>
  );
}

export default Questions;